import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Truck } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { formatINR } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { free_shipping_threshold, shipping_fee, allowed_cities, delivery_note, brand_email, brand_phone } = useSettings();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all fields");
      return;
    }
    toast.success("Thanks! We'll get back to you soon.");
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="fade-up max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9E3B21] mb-4">✦ Contact & Delivery</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold">Say Namaste</h1>
        <p className="mt-4 text-[#6E675F] max-w-xl mx-auto">Questions about orders, bulk requests, or a specific pickle craving — we'd love to hear from you.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#9E3B21]/10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-[#9E3B21]" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold">Delivery Zone</h3>
                <p className="text-sm text-[#6E675F] mt-1 leading-relaxed">
                  {delivery_note || `We currently deliver across ${(allowed_cities || ["Delhi NCR"]).join(", ")}.`} Free delivery on orders above {formatINR(free_shipping_threshold || 2000)}. Standard delivery fee {formatINR(shipping_fee || 49)}. Typical delivery: 2–4 days.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#D9822B]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#D9822B]" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold">Kitchen Address</h3>
                <p className="text-sm text-[#6E675F] mt-1">Dadu&apos;s Achaar Home Kitchen, Delhi NCR (visits by prior appointment only)</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#5F7355]/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-[#5F7355]" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold">Phone & WhatsApp</h3>
                <p className="text-sm text-[#6E675F] mt-1">{brand_phone ? `+91 ${brand_phone}` : "Coming soon"} · Mon–Sat, 10 AM to 7 PM</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-[#E5A83B]/15 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#D9822B]" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold">Email</h3>
                <p className="text-sm text-[#6E675F] mt-1">{brand_email || "hello@dadusachaar.com"}</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E6DEC3] rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="w-5 h-5 text-[#9E3B21]" />
            <h2 className="font-serif text-xl font-semibold">Send us a message</h2>
          </div>
          <div>
            <Label>Name</Label>
            <Input data-testid="contact-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="contact-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea data-testid="contact-message" rows={5} className="mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <Button data-testid="contact-submit" type="submit" className="btn-primary rounded-full w-full py-6">Send Message</Button>
        </form>
      </div>
    </div>
  );
}
